<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import vSelect from 'vue-select';

import IconChevronDown from '@/components/icons/IconChevronDown.vue';
import Loader from '@/components/ui/Loader.vue';

type keyType = keyof typeof model.value

interface Props {
  inputId: string
  options: any[]
  placeholder?: string
  label?: string
  index?: string
  labelText?: string
  required?: boolean
  width?: string
  parseValueToString?: boolean
  closeOnSelect?: boolean
  disabled?: boolean
  searchPlaceholder?: string
  clearable?: boolean
  hideSearch?: boolean
  fetchFunction?: (searchValue: string) => Promise<void>
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: '',
  label: '',
  index: '',
  labelText: '',
  required: false,
  width: '415px',
  parseValueToString: false,
  closeOnSelect: true,
  disabled: false,
  searchPlaceholder: 'Search',
  clearable: true,
  hideSearch: false
});

const model = defineModel<Record<string, unknown>[] | Record<string, unknown> | undefined>();
const dropdownSelect = ref();
const optionsData = ref();
const isLoading = ref(false);

const prepareOptions = () => {
  optionsData.value = props.options.map((element: unknown) => {
    return typeof element === 'object' ? element : { label: element };
  });
};

const getOptionLabel = (option: any) => {
  return typeof option === 'object' ? option[props.label ? props.label : 'label'] : option;
};

const toggleDropdown = ($event: unknown) => {
  dropdownSelect.value.toggleDropdown($event);
};

const fetchOptions = async (searchValue: string) => {
  if (optionsData.value && searchValue == undefined || !props.fetchFunction) return;
  isLoading.value = true;
  optionsData.value = await props.fetchFunction(searchValue);
  isLoading.value = false;
};

const showPlaceholder = computed(() => {
  return model.value instanceof Array ? model.value.length === 0 : !model.value;
});

const firstSelectedText = computed(() => {
  if (!model.value) return '';
  const keyName = (props.label ? props.label : 'label') as keyType;

  return model.value[keyName];
});

onMounted(() => {
  if (!props.fetchFunction) {
    prepareOptions();
  }
});
</script>

<template>
  <div
    :style="`width: ${width}`"
    class="input-select"
    :class="{
      'input-select--disabled': disabled,
      'input-select--hide-search': hideSearch
    }"
  >
    <label
      v-if="labelText"
      :for="inputId"
      :class="{ 'input-select__label--required': required }"
      class="input-select__label"
    >{{ labelText }}</label>
    <v-select
      :id="inputId"
      ref="dropdownSelect"
      v-model="model"
      :options="optionsData"
      :clearable="clearable"
      :close-on-select="closeOnSelect"
      :placeholder="placeholder"
      :index="index"
      :label="label"
      :disabled="disabled"
      :search-placeholder="searchPlaceholder"
      :get-option-label="getOptionLabel"
      :searchable="!hideSearch"
      @open="fetchOptions"
      @search="fetchOptions"
    >
      <template #no-options>
        <Loader
          v-if="isLoading"
          class="select-loader"
        />
      </template>

      <template #search="{ attributes, events }">
        <input
          class="vs__search"
          v-bind="attributes as Object"
          :placeholder="searchPlaceholder"
          v-on="events"
        >
      </template>

      <template #option="option">
        {{ getOptionLabel(option) }}
      </template>

      <template #header>
        <div
          class="input-select__content"
          @click="toggleDropdown"
        >
          <span
            v-if="showPlaceholder"
            class="placeholder"
          >
            {{ placeholder }}
          </span>
          <span v-else>
            <span>{{ `${firstSelectedText}` }}</span>
          </span>
        </div>
      </template>

      <template #open-indicator>
        <IconChevronDown class="vs__open-indicator" />
      </template>
    </v-select>
  </div>
</template>

<style src="vue-select/dist/vue-select.css"></style>

<style lang="scss" scoped>
.input-select {
  --input-height: 40px;
  --option-padding-x: 15px;
  --option-padding-y: 3px;
  --vs-border-radius: 2px;
  --vs-border-color: var(--border-default);
  --vs-component-placeholder-color: var(--placeholder-color);
  --vs-dropdown-option-padding: var(--option-padding-y) var(--option-padding-x);
  --vs-state-active-color: var(--blue);
  --vs-font-size: var(--placeholder-font-size);

  .placeholder {
    font-size: var(--placeholder-font-size);
    color: var(--placeholder-color);
  }

  &:deep(.text-ellipsis) {
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 180px;
    overflow: hidden;
  }

  &:deep(.select-loader) {
    max-width: 28px;
    margin: 0 auto;
  }

  &:deep(.vs__dropdown-toggle) {
    position: relative;
    background: var(--white);
    height: var(--input-height);
    cursor: pointer;
  }

  &:deep(.vs__dropdown-option) {
    height: var(--input-height);
    color: var(--text-color);
    display: flex;
    align-items: center;
    border-bottom: 1px solid var(--border-default);
  }

  &:deep(.vs__dropdown-option--highlight) {
    background-color: var(--gray);
    color: var(--blue);
  }

  &:deep(.vs__dropdown-option--selected, .vs__dropdown-option--highlight) {
    font-weight: 500;
  }

  &:deep(.vs__selected-text) {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    width: calc(100% - 3rem);
    height: var(--input-height);
    z-index: 1;
    cursor: pointer;
  }

  &:deep(.vs__no-options) {
    padding: var(--option-padding-y) var(--option-padding-x);
    height: var(--input-height);
    display: flex;
    align-items: center;
  }

  &:deep(.vs__selected) {
    display: none;
  }

  &:deep(.vs__actions) {
    position: absolute;
    padding: 0;
    top: 50%;
    right: 1rem;
    transform: translateY(-50%);

    .vs--open & {
      .vs__open-indicator {
        transform: rotate(0deg);
      }
    }
  }

  &:deep(.vs--open) {
    .vs__search {
      top: 100%;
      z-index: 1;
      opacity: 1;
    }
  }

  &:deep(.vs__selected-options) {
    padding: 0;
    position: absolute;
    top: 100%;
    left: -1px;
    right: -1px;
  }

  &:deep(.vs__search) {
    position: absolute;
    top: 100%;
    z-index: -1;
    opacity: 0;
    border-color: var(--border-default);
    background: var(--white) url(../../assets/svg/search.svg) no-repeat var(--option-padding-x) center;
    background-size: 16px;
    min-width: var(--vs-dropdown-min-width);

    &,
    &:focus {
      margin: 0;
      width: 100%;
      left: 0;
      border-left: 1px solid var(--border-default);
      height: var(--input-height);
      padding: var(--option-padding-y) var(--option-padding-x) var(--option-padding-y) 40px;
    }

    &::placeholder {
      font-size: var(--placeholder-font-size);
      color: var(--placeholder-color);
    }
  }

  &:deep(.vs__dropdown-menu) {
    top: calc(var(--input-height) * 2 - 2px);
    max-height: 202px;
    min-height: var(--input-height);
    padding: 0;
    border-top: 1px solid var(--border-default);
  }
}

.input-select__label {
  display: block;
  position: relative;
  font-size: 12px;
  font-weight: 700;
  line-height: 0.75;
  margin-bottom: 10px;
  width: v-bind(width);
}

.input-select__content {
  position: absolute;
  top: 0;
  bottom: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  padding: 0 10px;
}

.input-select--disabled {
  :deep(.vs__dropdown-toggle) {
    background: var(--gray);
  }
}

.input-select--hide-search {
  &:deep(.vs__dropdown-menu) {
    top: calc(var(--input-height) * 1 - 1px);
  }
}
</style>
